import JSZip from 'jszip';
import { generatePayStubPDF } from './pdfGenerator';
import { parseLocalDateForDisplay } from './payrollPeriodCalculator';
import type { PayrollEntry, Employee, CompanySettings } from '../types';

export const generatePayStubsZip = async (
  entries: PayrollEntry[],
  employee: Employee,
  companySettings?: CompanySettings | null
) => {
  if (!entries || entries.length === 0) {
    throw new Error('No paystubs available to download');
  }

  try {
    const zip = new JSZip();

    // Sort entries by date
    const sortedEntries = [...entries].sort((a, b) => 
      parseLocalDateForDisplay(a.payPeriod.start).getTime() - parseLocalDateForDisplay(b.payPeriod.start).getTime()
    );

    // Track successful generations
    let successCount = 0;

    // Generate PDFs for each entry
    for (const entry of sortedEntries) {
      try {
        const doc = await generatePayStubPDF(entry, employee, true, companySettings);
        if (doc) {
          const pdfOutput = doc.output('arraybuffer');
          const fileName = `paystub-${employee.lastName}-${parseLocalDateForDisplay(entry.payPeriod.end).toISOString().split('T')[0]}.pdf`;
          zip.file(fileName, pdfOutput);
          successCount++;
        }
      } catch (error) {
        console.error(`Error generating PDF for pay period ${entry.payPeriod.start} - ${entry.payPeriod.end}:`, error);
        // Continue with other PDFs even if one fails
        continue;
      }
    }

    if (successCount === 0) {
      throw new Error('Failed to generate any paystub PDFs');
    }

    // Generate and download the zip file
    const content = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 6
      }
    });

    // Create and trigger download
    const url = window.URL.createObjectURL(content);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paystubs-${employee.lastName}-${new Date().getFullYear()}.zip`;
    
    // Append, click, and cleanup
    document.body.appendChild(a);
    a.click();
    
    // Cleanup after small delay to ensure download starts
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 100);

    return successCount;
  } catch (error) {
    console.error('Error generating zip file:', error);
    throw new Error(
      error instanceof Error 
        ? error.message 
        : 'Failed to generate paystub archive. Please try again.'
    );
  }
};